import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PrizeManagement = ({ challenges, gymId, onRefresh }) => {
  const [selectedChallenge, setSelectedChallenge] = useState(null);

  const getChallengeStatus = (challenge) => {
    const now = new Date();
    const startDate = new Date(challenge?.start_date);
    const endDate = new Date(challenge?.end_date);

    if (now < startDate) return { label: 'Upcoming', color: 'bg-blue-100 text-blue-800' };
    if (now > endDate) return { label: 'Completed', color: 'bg-gray-100 text-gray-800' };
    return { label: 'Active', color: 'bg-green-100 text-green-800' };
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const challengesWithPrizes = challenges?.filter(c => c?.prize_description) || [];

  if (challengesWithPrizes?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <Icon name="Gift" className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Prize Challenges</h3>
        <p className="text-gray-600">Challenges with prizes will appear here for management</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-purple-100 p-3 rounded-lg">
            <Icon name="Gift" className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Prize Management</h3>
            <p className="text-sm text-gray-600">Track and manage challenge prizes and rewards</p>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-orange-900">
              <p className="font-medium mb-1">Prize Distribution Guidelines:</p>
              <ul className="list-disc list-inside space-y-1 text-orange-800">
                <li>Verify challenge completion before awarding prizes</li>
                <li>Announce winners publicly for community celebration</li>
                <li>Document prize fulfillment for record keeping</li>
                <li>Follow up with winners within 48 hours</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {challengesWithPrizes?.map((challenge) => {
          const status = getChallengeStatus(challenge);

          return (
            <div
              key={challenge?.id}
              className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${status?.color}`}>
                        {status?.label}
                      </span>
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        <Icon name="Gift" className="w-3 h-3 inline mr-1" />
                        PRIZE CHALLENGE
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{challenge?.title}</h3>
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Icon name="Gift" className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-purple-900 mb-1">Prize Details</p>
                        <p className="text-sm text-purple-800">{challenge?.prize_description}</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Participants</p>
                      <p className="text-lg font-bold text-gray-900">
                        {challenge?.current_participants || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Points Reward</p>
                      <p className="text-lg font-bold text-orange-600">
                        {challenge?.points_reward}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Duration:</span>
                    <span className="font-semibold text-gray-900">
                      {formatDate(challenge?.start_date)} - {formatDate(challenge?.end_date)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Challenge Type:</span>
                    <span className="font-semibold text-gray-900">
                      {challenge?.challenge_type?.replace('_', ' ')?.toUpperCase()}
                    </span>
                  </div>
                </div>

                <Button
                  onClick={() => setSelectedChallenge(challenge)}
                  className="w-full"
                >
                  <Icon name="Eye" className="w-4 h-4 mr-2" />
                  View Details
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Prize Details Modal */}
      {selectedChallenge && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">{selectedChallenge?.title}</h2>
                <button
                  onClick={() => setSelectedChallenge(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="X" className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Gift" className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-purple-900">Prize Information</h3>
                  </div>
                  <p className="text-purple-800">{selectedChallenge?.prize_description}</p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Challenge Details</h3>
                  <p className="text-gray-600">{selectedChallenge?.description}</p>
                </div>

                {selectedChallenge?.rules && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Rules</h3>
                    <p className="text-gray-600">{selectedChallenge?.rules}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Total Participants</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {selectedChallenge?.current_participants || 0}
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Points Reward</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {selectedChallenge?.points_reward}
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="Info" className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-900">
                      <p className="font-medium mb-1">Next Steps:</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-800">
                        <li>Monitor participant progress regularly</li>
                        <li>Verify completion criteria are met</li>
                        <li>Announce winners publicly</li>
                        <li>Coordinate prize distribution</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => setSelectedChallenge(null)}
                  className="w-full"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrizeManagement;